import { create } from 'zustand';

/**
 * Auth state (PROTOCOL §1).
 *  - `jwt`: username/password login → `Authorization: Bearer <access>`, refreshed with `refresh`.
 *  - `token`: personal API token → `Authorization: Token <t>`.
 *    `scheme: 'Bearer'` is a compatibility mode for the Node reference server (ADMIN_TOKEN as Bearer,
 *    `/ws/ui` without trailing slash); it is only chosen when the server rejects `Token <t>`.
 */
export type Auth =
  | { kind: 'jwt'; access: string; refresh: string; username?: string }
  | { kind: 'token'; token: string; scheme: 'Token' | 'Bearer' };

interface AuthState {
  auth: Auth | null;
  setAuth: (a: Auth | null) => void;
  setAccess: (access: string) => void;
}

const KEY = 'cruzr.auth';

function load(): Auth | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const a = JSON.parse(raw) as Auth;
    if (a.kind === 'jwt' && a.access && a.refresh) return a;
    if (a.kind === 'token' && a.token) return a;
  } catch {
    /* ignore */
  }
  return null;
}

function save(a: Auth | null) {
  try {
    if (a) localStorage.setItem(KEY, JSON.stringify(a));
    else localStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
}

export const useAuth = create<AuthState>((set, get) => ({
  auth: load(),
  setAuth: (auth) => {
    save(auth);
    set({ auth });
  },
  setAccess: (access) => {
    const a = get().auth;
    if (a?.kind !== 'jwt') return;
    const next = { ...a, access };
    save(next);
    set({ auth: next });
  },
}));

export function authHeader(a: Auth | null): string | null {
  if (!a) return null;
  if (a.kind === 'jwt') return `Bearer ${a.access}`;
  return `${a.scheme} ${a.token}`;
}

/** Token for `?token=` query params (WebSocket, media element URLs). */
export function queryToken(a: Auth | null): string {
  if (!a) return '';
  return a.kind === 'jwt' ? a.access : a.token;
}

export function isLegacy(a: Auth | null): boolean {
  return a?.kind === 'token' && a.scheme === 'Bearer';
}

/** Legacy Bearer fallback for non-JWT tokens: allowed in dev, or when VITE_LEGACY_BEARER=1. */
export const legacyBearerAllowed = import.meta.env.DEV || import.meta.env.VITE_LEGACY_BEARER === '1';
